/**
 * SimpleStorefrontCell — Status badge + inline toggle for Simple view.
 *
 * Shows Active/Incomplete/Draft badge alongside a small toggle switch.
 * On the first-ever toggle to "enabled", fires confetti + toast with store URL
 * (the Aha moment — user sees their product go live on their real store).
 */

import { useState, useEffect, useRef } from "react";
import { Switch } from "@/components/ui/switch";
import { ExternalLink } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import { toast } from "sonner";
import { fireCelebrationConfetti } from "@/components/layout/OnboardingCelebration";
import { StorefrontPhonePreview } from "./StorefrontPhonePreview";

const CELEBRATION_KEY = "aha_first_product_celebrated";

type Props = {
  itemName: string;
  storefrontEnabled: boolean;
  hasImage: boolean;
  hasPrice: boolean;
  storefrontUrl: string | null;
  productSlug: string;
  productName: string;
  language: string;
  /** Template item name when this row is a variant; null/empty for templates & standalone items. */
  variantOf?: string | null;
};

/**
 * Build the storefront product URL for the preview.
 * The storefront serves ONE page per template (variants are selected on that page),
 * so variant rows must point at their template — never at the variant's own code,
 * which 404s. `slugOrName` is the template's storefront_slug (preferred) or its name.
 */
function buildProductUrl(storefrontUrl: string, slugOrName: string): string {
  return `${storefrontUrl}/product/${slugOrName}`;
}

export function SimpleStorefrontCell({
  itemName, storefrontEnabled, hasImage, hasPrice,
  storefrontUrl, productSlug, productName, language, variantOf,
}: Props) {
  const isVariant = !!variantOf;
  const [localEnabled, setLocalEnabled] = useState(storefrontEnabled);
  const queryClient = useQueryClient();
  const cellRef = useRef<HTMLDivElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => { setLocalEnabled(storefrontEnabled); }, [storefrontEnabled]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (enabled: boolean) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.api_visibility_toggle.toggle_storefront_visibility",
        { item_code: itemName, enabled: enabled ? 1 : 0 },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["list", "Item"] });
    },
  });

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const prev = localEnabled;
    const next = !localEnabled;
    setLocalEnabled(next);

    try {
      await mutateAsync(next);

      // First-ever product enabled → celebration (Aha moment).
      // Skip the "View" action for variants — they have no standalone storefront page.
      if (next && !localStorage.getItem(CELEBRATION_KEY)) {
        localStorage.setItem(CELEBRATION_KEY, new Date().toISOString());
        fireCelebrationConfetti(cellRef.current);
        const productUrl = storefrontUrl && !isVariant ? buildProductUrl(storefrontUrl, productSlug) : null;
        toast.success(
          language === "th"
            ? `${productName} เผยแพร่แล้ว!`
            : `${productName} is now live!`,
          {
            description: productUrl
              ? (language === "th" ? "ดูบนหน้าร้านของคุณ" : "View on your store")
              : undefined,
            action: productUrl
              ? { label: language === "th" ? "ดูสินค้า" : "View", onClick: () => window.open(productUrl, "_blank") }
              : undefined,
            duration: 6000,
          },
        );
      }
    } catch {
      setLocalEnabled(prev);
      toast.error(language === "th" ? "ไม่สามารถอัปเดตได้" : "Failed to update");
    }
  };

  const isActive = localEnabled;
  // Only offer a storefront preview when the product is ACTUALLY live and viewable:
  //  • use the PERSISTED enabled state, not the optimistic toggle — a freshly-toggled
  //    item isn't resolvable on the storefront yet (would 404 in the preview iframe).
  //  • never for variants — the storefront has no standalone page for a variant code,
  //    so `/product/{variant-code}` returns a real 404 (the reported bug).
  const productUrl = storefrontEnabled && storefrontUrl && !isVariant
    ? buildProductUrl(storefrontUrl, productSlug)
    : null;

  const badgeClass = isActive && hasImage && hasPrice
    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
    : isActive
      ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
      : "bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400";

  const dotClass = isActive && hasImage && hasPrice
    ? "bg-emerald-500"
    : isActive
      ? "bg-amber-500"
      : "bg-neutral-400 dark:bg-neutral-500";

  const label = isActive && hasImage && hasPrice
    ? (language === "th" ? "เผยแพร่" : "Active")
    : isActive
      ? (language === "th" ? "ไม่สมบูรณ์" : "Incomplete")
      : (language === "th" ? "ฉบับร่าง" : "Draft");

  // "Incomplete" refinement — surface exactly what's missing (Zeigarnik nudge).
  const missingParts = isActive && !(hasImage && hasPrice)
    ? [
        !hasImage ? (language === "th" ? "รูปภาพ" : "image") : null,
        !hasPrice ? (language === "th" ? "ราคา" : "price") : null,
      ].filter(Boolean)
    : [];
  const missingHint = missingParts.length > 0
    ? `${language === "th" ? "ขาด" : "Missing"}: ${missingParts.join(", ")}`
    : null;

  return (
    <div ref={cellRef} className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
      <span className="inline-flex flex-col items-start gap-0.5 shrink-0">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${badgeClass}`}>
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass}`} />
          {label}
        </span>
        {missingHint && (
          <span className="text-[10px] text-amber-600/80 dark:text-amber-400/70 whitespace-nowrap pl-0.5">
            {missingHint}
          </span>
        )}
      </span>
      <Switch
        checked={localEnabled}
        onCheckedChange={() => {}}
        onClick={handleToggle}
        disabled={isPending}
        aria-label={language === "th" ? "เผยแพร่บนหน้าร้าน" : "Publish to storefront"}
        className="scale-[0.65] origin-center shrink-0"
      />
      {productUrl ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setPreviewUrl(productUrl); }}
          className="inline-flex items-center gap-1 text-[12px] text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors whitespace-nowrap shrink-0"
        >
          <ExternalLink className="w-3 h-3 shrink-0" />
          {language === "th" ? "ดูสินค้า" : "View"}
        </button>
      ) : null}
      {previewUrl && (
        <StorefrontPhonePreview
          url={previewUrl}
          productName={productName}
          onClose={() => setPreviewUrl(null)}
          language={language}
        />
      )}
    </div>
  );
}
